<template>
  <div @keyup.esc="handleClose()" class="flex flex-col overflow-y-auto pb-2 rounded">
    <div :class="['px-2 mb-2.5 w-full', { 'mt-9': isFullscreen }]">
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center space-x-2.5 bg-gray-100 rounded-full py-1.5 px-4 flex-1 min-w-0 overflow-hidden">
          <button @click="previousUser(userSelected)" class="flex-shrink-0 hover:text-primary">
            <i class="fa-solid fa-angle-left text-xs"></i>
          </button>
          <div class="flex items-center space-x-2 font-manrope flex-1 min-w-0 overflow-hidden whitespace-nowrap">
            <img :src="userSelected.user.avatar_url" class="flex-shrink-0 rounded-full w-6 h-6" />
            <span class="font-semibold text-gray-800 truncate">
              {{ userSelected.user.name }}
            </span>
          </div>
          <button @click="nextUser(userSelected)" class="flex-shrink-0 hover:text-primary">
            <i class="fa-solid fa-angle-right text-xs"></i>
          </button>
        </div>
        <div class="flex items-center space-x-1 ml-4 flex-shrink-0">
          <button class="text-gray-400 w-7 h-7 rounded-full bg-gray-100 flex-shrink-0">
            <i class="fa-solid fa-bullhorn text-xs"></i>
            {{ totalTurns }}
          </button>
          <button class="text-gray-600 w-7 h-7 rounded-full hover:bg-gray-100 flex-shrink-0" v-my-tooltip.left="'Send Nudge'" @click="$emit('openSendNudge', true, userSelected)">
            <i class="fa-solid fa-hand-pointer"></i>
          </button>
          <button @click="handleClose" class="text-gray-800 w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center flex-shrink-0">
            <i class="fa-solid fa-close text-sm"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="flex flex-col space-y-2" :class="isFullscreen ? 'max-height-list-fullscreen' : 'max-height-list'">
      <div class="px-2">
        <div @click="toggleTabs('studentNotes')" class="bg-gray-100 rounded flex justify-between px-3 py-1.5 cursor-pointer">
          <h5 class="font-semibold text-gray-800">Student Notes</h5>
          <button class="text-gray-800">
            <i :class="isOpenStudentNotes === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid fa-angle-down"></i>
          </button>
        </div>
        <textarea
          v-show="isOpenStudentNotes === true"
          v-model="userSelected.notes.content"
          :placeholder="userSelected.notes.content === '' ? 'There are no notes yet' : ''"
          class="w-full border-solid border border-gray-200 mt-2 px-2.5 py-2 rounded outline-none"
          :rows="notesRows"
          ref="textareaNotes"
          disabled
        ></textarea>
      </div>
      <div class="px-2">
        <div @click="toggleTabs('teacherNotes')" class="bg-gray-100 rounded mb-2 flex justify-between px-3 py-1.5 cursor-pointer">
          <h5 class="font-semibold text-gray-800">Teacher Notes</h5>
          <button class="text-gray-800">
            <i :class="isTeacherNotesOpen === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid fa-angle-down"></i>
          </button>
        </div>
        <TeacherNotes v-if="isTeacherNotesOpen === true" :participation="getStudentSummaryLD" />
      </div>
      <div class="px-2">
        <div @click="toggleTabs('assessment')" class="bg-gray-100 rounded flex justify-between px-3 py-1.5 cursor-pointer">
          <h5 class="font-semibold text-gray-800">Assessment</h5>
          <button class="text-gray-800">
            <i :class="isOpenAssessment === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid"></i>
          </button>
        </div>
        <div v-if="isOpenAssessment === true && hasTeacherPermissionsInCourse">
          <LiveDiscussionPointsBar
            v-if="participation !== null || participation !== undefined"
            @editAssessment="$emit('editAssessment')"
            @saveAssessment="saveAssessment"
            :showEditAssessment="true"
            :participation="userSelected"
          />
        </div>
      </div>
      
      <div class="px-2">
        <div id="open-student-feedback" @click="toggleTabs('feedback')" class="bg-gray-100 rounded flex justify-between px-3 py-1.5 cursor-pointer">
          <div class="font-semibold text-gray-800">
            Feedback
            <span v-if="hasTeacherPermissionsInCourse && hasFeedback" class="text-green-500">
              (Sent)
            </span>
            <span v-else-if="hasTeacherPermissionsInCourse && draftFeedback" class="text-yellow-400">
              (Draft)
            </span>
          </div>
          <button class="text-gray-800">
            <i :class="isOpenFeedback === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid fa-angle-down"></i>
          </button>
        </div>
        <div v-if="isOpenFeedback === true">
          <div class="w-full rounded outline-none focus-within:border-primary border border-solid border-gray-200">
            <textarea
              class="p-2.5 w-full h-full rounded outline-none"
              rows="6"
              ref="feedback-textarea"
              v-model="selectedSuggestion"
              :disabled="!hasTeacherPermissionsInCourse || !edit_feedback"
              :placeholder="'Send private feedback to ' + userSelected.user.name"
              @blur="saveDraftFeedback"
            ></textarea>
            <div v-if="hasTeacherPermissionsInCourse && edit_feedback" class="flex justify-between items-center p-2 border-t">
              <button @click="showTemplateModal" class="sample flex items-center p-1 rounded hover:bg-gray-100 space-x-1.5 text-gray-600">
                <img src="/static/roundtable_icons/templates.svg" class="w-4 h-4" />
                <span>Templates</span>
              </button>
              <div>
                <span v-if="saving" class="pulsate text-xs px-1">
                  Saving...
                </span>
                <span v-if="saved" class="text-xs px-1">Saved</span>
              </div>
            </div>
          </div>
          <button
            v-if="hasTeacherPermissionsInCourse && edit_feedback"
            @click="sendFeedbackToStudent()"
            class="sample bg-primary rounded py-2 px-4 my-3 text-white font-semibold w-full"
          >
            {{ hasFeedback ? 'Update Feedback' : 'Send Feedback' }}
          </button>
          <button
            v-else-if="hasTeacherPermissionsInCourse && !edit_feedback"
            @click="edit_feedback = true"
            class="sample py-2 px-4 my-3 w-full bg-gray-400 hover:bg-gray-500 rounded text-white font-semibold"
          >
            Edit Feedback
          </button>
        </div>
      </div>
      <div class="px-2">
        <div @click="toggleTabs('tapInDetails')" class="bg-gray-100 rounded flex justify-between px-3 py-1.5 cursor-pointer">
          <h5 class="font-semibold text-gray-800">Participation Details</h5>
          <button class="text-gray-800">
            <i :class="isOpenTapInDetails === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid fa-angle-down"></i>
          </button>
        </div>
        <div class="my-2.5" v-if="isOpenTapInDetails">
          <TapInStudent
            :tapInStudentDetails="getStudentSummaryLD.times_spoken"
            :isStudentDetails="true"
          />
        </div>
      </div>
      <div class="px-2">
        <div @click="toggleTabs('nudges')" class="bg-gray-100 rounded flex justify-between px-3 py-1.5 cursor-pointer">
          <h5 class="font-semibold text-gray-800">Nudges</h5>
          <button class="text-gray-800">
            <i :class="isOpenNudges === true ? 'fa-angle-down' : 'fa-angle-up'" class="fa-solid fa-angle-down"></i>
          </button>
        </div>
        <div v-if="isOpenNudges" class="m-3 h-48 overflow-y-auto sample">
          <div v-if="!getStudentSummaryLD.nudges.length">
            No nudges during this RoundTable
          </div>
          <div v-else class="mt-3">
            <StudentNudges :nudges="getStudentSummaryLD.nudges" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import liveDiscussionService from 'services/liveDiscussion';
  import { EventBus } from '../../../../utils/event-bus';
  import store from 'store';

  export default Vue.extend({
    data() {
      return {
        isOpenAssessment: true,
        isOpenTeacherNotes: true,
        isOpenFeedback: false,
        isTeacherNotesOpen: true,
        isOpenTapInDetails: false,
        isOpenStudentNotes: true,
        isOpenNudges: false,
        observations: '',
        userSelected: {},
        takenSuggestions: [],
        isMySuggestions: false,
        isAutomaticSuggestions: false,
        savedFeedback: [],
        selectedSuggestion: '',
        participation: {},
        isSavingTeacherNotes: false,
        collapsed_saved_feedback: true,
        collapsed_suggested_feedback: true,
        show_saved_feedback_modal: false,
        show_import_feedback_modal: false,
        feedback_content: '',
        feedback_id: '',
        set_edit_saved_feedback: false,
        show_confirm_delete_feedback: false,
        edit_feedback: false,
        hovered: false,
        saving: false,
        saved: false,
        notesRows: 7
      };
    },
    components: {
      Textbox: () => import('components/textbox.vue'),
      LiveDiscussionPointsBar: () =>
        import(
          'components/liveDiscussionPointsBar/liveDiscussionPointsBar.vue'
        ),
      Tooltip: () => import('components/Tooltip.vue'),
      TapInStudent: () => import('./IndividualStudentTapInStatistics.vue'),

      TeacherNotes: () => import('./TeacherNotes.vue'),
      FeedbackStudent: () => import('./FeedbackStudent.vue'),
      StudentNudges: () => import('./Nudges.vue'),
    },
    watch: {
      getStudentSummaryLD() {
        this.userSelected = this.getStudentSummaryLD;
        this.observations = this.userSelected.observations
          ? this.userSelected.observations[0]
          : '';

        this.selectedSuggestion =
          this.userSelected.feedback_for_student &&
          this.userSelected.feedback_for_student.length &&
          this.userSelected.feedback_for_student[0] !== ''
            ? this.userSelected.feedback_for_student[0]
            : this.userSelected.draft_feedback_for_student;

        this.edit_feedback =
          this.userSelected.feedback_for_student &&
          this.userSelected.feedback_for_student.length &&
          this.userSelected.feedback_for_student[0] !== ''
            ? false
            : true;
      },
    },
    props: {
      userInformation: {
        type: Object,
        default: {},
      },
      isFullscreen: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters([
        'getCurrentDiscussion',
        'hasTeacherPermissionsInCourse',
        'getStudentSummaryLD',
        'getCourseDetail',
        'getProfile',
      ]),
      assessmentCriteria: function () {
        return this.getCurrentDiscussion.points_settings.peer_rewards;
      },
      handRaiseTypes() {
        const { new_idea, build_on, challenge, question, hand_raise } =
          this.getStudentSummaryLD.times_spoken;
        const byTeacher = hand_raise;
        return { new_idea, build_on, challenge, question, byTeacher };
      },
      automatedFeedback: function () {
        return this.getStudentSummaryLD.automated_feedback;
      },
      hasFeedback() {
        return (
          this.getStudentSummaryLD.feedback_for_student &&
          this.getStudentSummaryLD.feedback_for_student.length &&
          this.getStudentSummaryLD.feedback_for_student[0] !== ''
        );
      },
      draftFeedback() {
        return this.getStudentSummaryLD.draft_feedback_for_student !== '';
      },
    },
    methods: {
      handleResize() {
        const textarea = this.$refs.textareaNotes;
        if (textarea) {
          const { offsetHeight } = textarea;
          const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10);
          const rows = Math.ceil(offsetHeight / lineHeight);
          sessionStorage.setItem("notesSize", rows);
        }
      },
      handleClose: async function() {
        this.$emit('backToParticipantsList');
        EventBus.$emit('resize-section', true);
      },
      toggleTabs: function (tab) {
        const tabs = {
          assessment: () => (this.isOpenAssessment = !this.isOpenAssessment),
          feedback: () => (
            (this.isOpenFeedback = !this.isOpenFeedback),
            (this.isMySuggestions = false),
            (this.isAutomaticSuggestions = false)
          ),
          tapInDetails: () => (this.isOpenTapInDetails = !this.isOpenTapInDetails),
          studentNotes: () => (this.isOpenStudentNotes = !this.isOpenStudentNotes),
          nudges: () => (this.isOpenNudges = !this.isOpenNudges),
          teacherNotes: () => (this.isTeacherNotesOpen = !this.isTeacherNotesOpen)
        };
        return tabs[tab]();
      },
      saveTeacherNote: function () {
        liveDiscussionService
          .observations(this.userInformation.user.id, [this.observations])
          .then((res) => {
            store.dispatch('updateParticipationSpread', res.data);
            return null;
          })
          .catch(console.error);
      },
      nextUser: function (user) {
        const participation_id = user.participation_id
          ? user.participation_id
          : user.id;
        const array = this.getCurrentDiscussion.participations.map((p) => p.id);
        const index = array.indexOf(participation_id);
        const newArray = array
          .splice(index, array.length)
          .concat(array.splice(0, index));
        const participationToOpen = newArray[1];
        store
          .dispatch('fetchParticipationSummary', participationToOpen)
          .then((student) => {
            this.observations = student.observations
              ? student.observations[0]
              : '';

            this.selectedSuggestion =
              student.feedback_for_student &&
              student.feedback_for_student.length &&
              student.feedback_for_student[0] !== ''
                ? student.feedback_for_student[0]
                : student.draft_feedback_for_student;

            this.edit_feedback =
              student.feedback_for_student &&
              student.feedback_for_student.length &&
              student.feedback_for_student !== ''
                ? false
                : true;
            return (this.userSelected = student);
          });
      },
      previousUser: function (user) {
        const participation_id = user.participation_id
          ? user.participation_id
          : user.id;
        const array = this.getCurrentDiscussion.participations.map((p) => p.id);
        const index = array.indexOf(participation_id);
        const newArray = array
          .splice(index, array.length)
          .concat(array.splice(0, index));
        const participationToOpen = newArray[newArray.length - 1];
        store
          .dispatch('fetchParticipationSummary', participationToOpen)
          .then((student) => {
            this.observations = student.observations
              ? student.observations[0]
              : '';

            this.selectedSuggestion =
              student.feedback_for_student &&
              student.feedback_for_student.length &&
              student.feedback_for_student[0] !== ''
                ? student.feedback_for_student[0]
                : student.draft_feedback_for_student;

            this.edit_feedback =
              student.feedback_for_student &&
              student.feedback_for_student.length &&
              student.feedback_for_student !== ''
                ? false
                : true;
            return (this.userSelected = student);
          });
      },
      saveTeacherObservations: function () {
        liveDiscussionService
          .observations(this.userSelected.id, [this.observations])
          .then((res) => {
            store.dispatch('updateParticipationSpread', res.data);
          })
          .catch(console.error);
      },
      sendFeedbackToStudent: function () {
        const feedback = {
          participation_id: this.userSelected.id,
          feedback_content: this.selectedSuggestion,
          auto_save: false,
        };
        store.dispatch('submitFeedbackForStudent', feedback).then(() => {
          const toast = {
            type: 'Success',
            message:
              'You send feedback to ' + this.userSelected.user.name + '!',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          this.userSelected.feedback_for_student = this.selectedSuggestion;
          this.edit_feedback = false;
        });
      },
      saveAssessment(assessment) {
        const assessments = this.mergedAssessments(assessment);
        store.dispatch('updateAssessment', {
          participationID: this.userSelected.id,
          payload: { assessments },
        });
      },
      mergedAssessments(assessment) {
        return this.getStudentSummaryLD.points_settings.peer_rewards.map(
          (pr) => {
            if (assessment.tag === pr.tag) {
              if (
                this.getCurrentDiscussion.point_cap &&
                parseFloat(assessment.value) >
                  this.getCurrentDiscussion.point_cap
              ) {
                pr.value = this.getCurrentDiscussion.point_cap;
              } else pr.value = assessment.value;
            } else {
              const existingAssessment =
                this.getStudentSummaryLD.assessments.find(
                  (a) => a.tag === pr.tag
                );
              if (existingAssessment) {
                if (
                  this.getCurrentDiscussion.point_cap &&
                  parseFloat(existingAssessment.value) >
                    this.getCurrentDiscussion.point_cap
                )
                  pr.value = this.getCurrentDiscussion.point_cap;
                else pr.value = existingAssessment.value;
              } else pr.value = 0;
            }
            return { tag: pr.tag, value: pr.value };
          }
        );
      },
      handleChangeTeacherNotes: function () {
        this.isSavingTeacherNotes = true;
        liveDiscussionService
          .observations(this.userSelected.id, [this.observations])
          .then((res) => {
            store.dispatch('updateParticipationSpread', res.data);
            this.isSavingTeacherNotes = false;
          })
          .catch(console.error);
      },
      showTemplateModal() {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        EventBus.$emit('showTemplateModal');
      },
      saveDraftFeedback() {
        if (
          this.getStudentSummaryLD.feedback_for_student &&
          this.getStudentSummaryLD.feedback_for_student.length &&
          this.getStudentSummaryLD.feedback_for_student[0] !== ''
        ) {
          return;
        }
        if (this.selectedSuggestion !== '') {
          this.saving = true;
          const payload = {
            participation_id: this.userSelected.id,
            feedback_content: this.selectedSuggestion,
          };
          this.$store
            .dispatch('submitDraftFeedbackForStudent', payload)
            .then(() => {
              this.saving = false;
              this.saved = true;
              setTimeout(() => {
                this.saved = false;
              }, 2000);
            })
            .catch((err) => {
              this.saving = false;
              console.error(err);
            });
        }
      },
    },
    mounted: async function() {
      EventBus.$emit('resize-section', true);
      this.userSelected = this.userInformation;
      this.observations = this.userSelected.observations ? this.userSelected.observations[0] : '';
      this.selectedSuggestion =
        this.userSelected.feedback_for_student &&
        this.userSelected.feedback_for_student.length &&
        this.userSelected.feedback_for_student[0] !== '' ? this.userSelected.feedback_for_student[0] : this.userSelected.draft_feedback_for_student;

      this.edit_feedback =
        this.userSelected.feedback_for_student &&
        this.userSelected.feedback_for_student.length &&
        this.userSelected.feedback_for_student[0] !== '' ? false : true;
      this.$route.query.feedback === 'open' ? (this.isOpenFeedback = true) : (this.isOpenFeedback = false);

      EventBus.$on('selectSavedFeedback', (value) => {
        if (this.selectedSuggestion && this.selectedSuggestion.length) {
          this.selectedSuggestion += `\n ${value}`;
        } else {
          this.selectedSuggestion = value;
        }
      });
      EventBus.$on('selectSuggestFeedback', (value) => {
        if (this.selectedSuggestion && this.selectedSuggestion.length) {
          this.selectedSuggestion += `\n ${value}`;
        } else {
          this.selectedSuggestion = value;
        }
      });
      EventBus.$on('assessment-focus', () => {
        this.$refs['feedback-textarea'].focus();
      });

      const notesRow = sessionStorage.getItem("notesSize");

      if (notesRow) {
        this.notesRows = notesRow;
      }

      this.$nextTick(() => {
      const textarea = this.$refs.textareaNotes;
        if (textarea) {
          window.addEventListener("mouseup", this.handleResize);
        } else {
          console.error("Textarea ref is undefined");
        }
      });
    },
    beforeDestroy() {
      EventBus.$off('selectSavedFeedback');
      EventBus.$off('selectSuggestFeedback');
      EventBus.$off('assessment-focus');
    },
  });
</script>
<style scoped>
  .max-height-list {
    max-height: calc(100vh - 220px);
    min-height: calc(100vh - 220px);
    overflow-y: scroll;
  }
  .max-height-list-fullscreen {
    max-height: calc(100vh - 60px);
    min-height: calc(100vh - 60px);
    overflow-y: scroll;
  }
</style>



// WEBPACK FOOTER //
// Participant.vue?7a783a00